import React from 'react';
import { HealerTrinketAnalysisData, HealerTrinketFightRow } from '../../types';
import HelpTip from '../../components/HelpTip';
import { aggregateHealerTrinketBossGroup, formatBossAggregateDuration } from '../healerTrinketAggregates';
import { BossFightGroup } from '../types';
import { groupFightsByBoss } from '../utils';

interface HealerTrinketComparisonGridProps {
    healers: HealerTrinketAnalysisData[];
}

interface AlignedBossGroup {
    key: string;
    name: string;
    fightsByHealer: HealerTrinketFightRow[][];
}

function groupMinStart(group: BossFightGroup<HealerTrinketFightRow>): number {
    return Math.min(...group.fights.map((fight) => (fight.reportStartTime ?? 0) + (fight.fightStartTime ?? 0)));
}

function alignBossGroups(healers: HealerTrinketAnalysisData[]): AlignedBossGroup[] {
    const groupsPerHealer = healers.map((healer) => groupFightsByBoss(healer.fights));
    const keyMeta = new Map<string, { name: string; minStart: number }>();

    for (const groups of groupsPerHealer) {
        for (const group of groups) {
            const minStart = groupMinStart(group);
            const existing = keyMeta.get(group.key);
            if (!existing || minStart < existing.minStart) {
                keyMeta.set(group.key, { name: group.name, minStart });
            }
        }
    }

    const orderedKeys = Array.from(keyMeta.entries()).sort((left, right) => {
        return left[1].minStart - right[1].minStart;
    });

    return orderedKeys.map(([key, meta]) => ({
        key,
        name: meta.name,
        fightsByHealer: groupsPerHealer.map((groups) => {
            const match = groups.find((group) => group.key === key);
            return match ? match.fights : [];
        }),
    }));
}

function equippedFractionSuffix(equipped: string): string {
    if (equipped === 'All' || equipped === '0' || equipped === '—') {
        return '';
    }
    return ` ${equipped}`;
}

function healerLabel(healer: HealerTrinketAnalysisData): string {
    return healer.selectedPlayer?.name ?? 'Unknown healer';
}

function healerClassLabel(healer: HealerTrinketAnalysisData): string {
    return healer.selectedPlayer?.class ?? '';
}

interface CompareCardProps {
    fights: HealerTrinketFightRow[];
    earringApplicable: boolean;
    mode: 'theoretical' | 'actual';
}

const CompareCard: React.FC<CompareCardProps> = ({ fights, earringApplicable, mode }) => {
    const agg = aggregateHealerTrinketBossGroup(fights, earringApplicable);
    const theoretical = mode === 'theoretical';

    const lines: Array<{ label: string; value: string; equipped?: string }> = [
        {
            label: 'BD',
            value: theoretical ? agg.blueDragonTheoreticalMp5 : agg.blueDragonMp5,
            equipped: theoretical ? undefined : agg.blueDragonEquipped,
        },
        {
            label: 'EoG',
            value: theoretical ? agg.eyeOfGruulTheoreticalMp5 : agg.eyeOfGruulMp5,
            equipped: theoretical ? undefined : agg.eyeOfGruulEquipped,
        },
    ];

    if (earringApplicable) {
        lines.push({
            label: 'EoSM',
            value: theoretical ? agg.earringTheoreticalMp5 : agg.earringMp5,
            equipped: theoretical ? undefined : agg.earringEquipped,
        });
    }

    return (
        <div className="trinket-compare-card">
            {lines.map((line) => (
                <div key={line.label} className="trinket-compare-line">
                    <span className="trinket-compare-label">
                        {line.label}
                        {line.equipped ? equippedFractionSuffix(line.equipped) : ''}
                    </span>
                    <span className="trinket-compare-value">{line.value}</span>
                </div>
            ))}
        </div>
    );
};

function groupSummary(fightsByHealer: HealerTrinketFightRow[][]): { fightCount: number; durationSeconds: number } {
    let fightCount = 0;
    let durationSeconds = 0;
    for (const fights of fightsByHealer) {
        if (fights.length > fightCount) {
            fightCount = fights.length;
        }
        const duration = fights.reduce((sum, fight) => sum + fight.durationSeconds, 0);
        if (duration > durationSeconds) {
            durationSeconds = duration;
        }
    }
    return { fightCount, durationSeconds };
}

const HealerTrinketComparisonGrid: React.FC<HealerTrinketComparisonGridProps> = ({ healers }) => {
    const groups = alignBossGroups(healers);
    const allFightsByHealer = healers.map((healer) => healer.fights);
    const allSummary = groupSummary(allFightsByHealer);

    return (
        <div className="table-container trinket-compare-wrap sticky-head">
            <table className="breakdown-table trinket-compare-table">
                <thead>
                    <tr>
                        <th rowSpan={2} className="trinket-compare-boss-col">
                            <span className="heading-with-help">
                                Boss
                                <HelpTip label="Theoretical vs actual">
                                    <p>
                                        <strong>Theoretical</strong> is what the trinket would have been worth if it was
                                        equipped.
                                    </p>
                                    <p>
                                        <strong>Actual</strong> is what it did on fights where it was equipped.
                                    </p>
                                </HelpTip>
                            </span>
                        </th>
                        {healers.map((healer, index) => (
                            <th key={`${healerLabel(healer)}-${index}`} colSpan={2} className="trinket-compare-healer">
                                {healerLabel(healer)}
                                {healerClassLabel(healer) ? (
                                    <span className="trinket-compare-class">{healerClassLabel(healer)}</span>
                                ) : null}
                            </th>
                        ))}
                    </tr>
                    <tr>
                        {healers.map((healer, index) => (
                            <React.Fragment key={`${healerLabel(healer)}-${index}-modes`}>
                                <th className="trinket-compare-mode">Theoretical</th>
                                <th className="trinket-compare-mode">Actual</th>
                            </React.Fragment>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {groups.length === 0 && (
                        <tr>
                            <td colSpan={1 + healers.length * 2}>No fights matched the selected filter.</td>
                        </tr>
                    )}
                    {groups.map((group) => {
                        const summary = groupSummary(group.fightsByHealer);
                        return (
                            <tr key={group.key}>
                                <td className="trinket-compare-boss-col">
                                    <div className="trinket-compare-boss-name">{group.name}</div>
                                    <div className="boss-group-count">
                                        {summary.fightCount} fight{summary.fightCount === 1 ? '' : 's'} ·{' '}
                                        {formatBossAggregateDuration(summary.durationSeconds)}
                                    </div>
                                </td>
                                {healers.map((healer, index) => {
                                    const fights = group.fightsByHealer[index];
                                    if (fights.length === 0) {
                                        return (
                                            <React.Fragment key={`${group.key}-${index}`}>
                                                <td className="trinket-compare-empty">—</td>
                                                <td className="trinket-compare-empty">—</td>
                                            </React.Fragment>
                                        );
                                    }
                                    const earringApplicable = healer.earring.applicable;
                                    return (
                                        <React.Fragment key={`${group.key}-${index}`}>
                                            <td>
                                                <CompareCard
                                                    fights={fights}
                                                    earringApplicable={earringApplicable}
                                                    mode="theoretical"
                                                />
                                            </td>
                                            <td>
                                                <CompareCard
                                                    fights={fights}
                                                    earringApplicable={earringApplicable}
                                                    mode="actual"
                                                />
                                            </td>
                                        </React.Fragment>
                                    );
                                })}
                            </tr>
                        );
                    })}
                    {groups.length > 0 && (
                        <tr className="trinket-compare-total">
                            <td className="trinket-compare-boss-col">
                                <div className="trinket-compare-boss-name">All fights</div>
                                <div className="boss-group-count">
                                    {allSummary.fightCount} fight{allSummary.fightCount === 1 ? '' : 's'} ·{' '}
                                    {formatBossAggregateDuration(allSummary.durationSeconds)}
                                </div>
                            </td>
                            {healers.map((healer, index) => {
                                const fights = allFightsByHealer[index];
                                if (fights.length === 0) {
                                    return (
                                        <React.Fragment key={`all-${index}`}>
                                            <td className="trinket-compare-empty">—</td>
                                            <td className="trinket-compare-empty">—</td>
                                        </React.Fragment>
                                    );
                                }
                                return (
                                    <React.Fragment key={`all-${index}`}>
                                        <td>
                                            <CompareCard
                                                fights={fights}
                                                earringApplicable={healer.earring.applicable}
                                                mode="theoretical"
                                            />
                                        </td>
                                        <td>
                                            <CompareCard
                                                fights={fights}
                                                earringApplicable={healer.earring.applicable}
                                                mode="actual"
                                            />
                                        </td>
                                    </React.Fragment>
                                );
                            })}
                        </tr>
                    )}
                </tbody>
            </table>
        </div>
    );
};

export default HealerTrinketComparisonGrid;
